import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AlertCircle, Check, ImageUp, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  createAd,
  createPortfolioItem,
  deleteAd,
  deletePortfolioItem,
  fetchAdminAds,
  fetchAdminPortfolio,
  updateAd,
  updatePortfolioItem,
  uploadImage,
  type Ad,
  type PortfolioItem,
} from "@/lib/content-client";
import { AdminChrome, type AdminTab } from "@/components/AdminChrome";

/* ------------------------------------------------------------- primitives */

const FIELD =
  "w-full border-b border-border bg-transparent pb-2.5 font-sans text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-gold disabled:opacity-50";

const LABEL = "mt-5 block font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase";

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      <span className="mt-2.5 block">{children}</span>
      {error ? (
        <span className="mt-1.5 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1.5 block font-sans text-[0.7rem] text-muted-foreground/70">{hint}</span>
      ) : null}
    </label>
  );
}

function Banner({ tone, children }: { tone: "error" | "ok"; children: ReactNode }) {
  return (
    <p
      className={cn(
        "flex items-center gap-1.5 font-sans text-[0.7rem]",
        tone === "error" ? "text-destructive" : "text-gold",
      )}
    >
      {tone === "error" ? (
        <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
      ) : (
        <Check className="h-3 w-3 shrink-0" strokeWidth={1.5} />
      )}
      {children}
    </p>
  );
}

function Th({ children, className }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-left font-sans text-[0.6rem] font-medium tracking-[0.16em] text-muted-foreground uppercase",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex cursor-pointer items-center gap-2.5"
    >
      <span
        className={cn(
          "relative h-5 w-9 rounded-full transition-colors duration-300",
          checked ? "bg-gold" : "bg-border",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-background transition-transform duration-300",
            checked ? "translate-x-4.5" : "translate-x-0.5",
          )}
        />
      </span>
      <span className="font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------ image field */

/**
 * Upload widget with a URL escape hatch.
 *
 * R2 may not be connected to this deployment yet, so the URL field is always
 * usable — an upload failure downgrades to a hint rather than blocking the
 * form. Whichever input is filled wins, and the resolved URL is previewed.
 */
type Size = { width: number; height: number };

/**
 * Reads an image's intrinsic pixel size in the browser.
 *
 * The public pages lay out from the real ratio, so the dimensions have to be
 * captured where the image is chosen rather than guessed server-side — an
 * uploaded file and a pasted URL are both measurable here, and neither can be
 * measured from the outside without fetching the whole asset.
 *
 * Resolves to null on any failure (bad URL, CORS, decode error) because a
 * missing measurement is fine: the renderer falls back to a default ratio
 * instead of the form being blocked.
 */
function measure(src: string): Promise<Size | null> {
  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      resolve(width > 0 && height > 0 ? { width, height } : null);
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function describeSize(size: Size | null, measured: boolean) {
  if (!size) {
    return measured
      ? "Could not read this image's size — the layout will use a default shape."
      : null;
  }
  const ratio = (size.width / size.height).toFixed(2);
  return `Measured ${size.width} × ${size.height} (ratio ${ratio}).`;
}

function ImageField({
  value,
  size,
  onChange,
  onSizeChange,
  folder,
  fieldError,
}: {
  value: string;
  size: Size | null;
  onChange: (next: string) => void;
  onSizeChange: (next: Size | null) => void;
  folder: string;
  fieldError?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [measured, setMeasured] = useState(false);
  const [note, setNote] = useState<{ tone: "error" | "ok"; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Measure whenever the image changes, so pasting a URL and uploading a file
  // both fill the dimensions in without the admin doing anything extra.
  //
  // The size is dropped the moment the URL changes rather than when the new
  // measurement arrives: otherwise a save landing in that window would store
  // the *previous* image's ratio, and the public page would lay the new picture
  // out at the old one's shape.
  const measuredFor = useRef(value);
  useEffect(() => {
    let live = true;
    if (!value) {
      measuredFor.current = "";
      onSizeChange(null);
      setMeasured(false);
      return;
    }

    if (measuredFor.current !== value) {
      measuredFor.current = value;
      onSizeChange(null);
    }

    void measure(value).then(result => {
      if (!live || measuredFor.current !== value) return;
      onSizeChange(result);
      setMeasured(true);
    });
    return () => {
      live = false;
    };
    // `onSizeChange` is stable enough for this: it only ever sets state on the
    // owning form, and including it would re-measure on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setNote(null);
    const result = await uploadImage(file, folder);
    setBusy(false);

    if (result.ok && result.url) {
      onChange(result.url);
      setNote({ tone: "ok", text: `Uploaded as ${result.key ?? "image"}.` });
      return;
    }
    // Not an error state when R2 is simply absent: the URL field covers it.
    setNote({
      tone: "error",
      text: result.needsMedia
        ? "Image storage is not connected yet — paste an image URL below instead."
        : (result.error ?? "Upload failed."),
    });
  };

  return (
    <div>
      <span className={LABEL}>Image</span>

      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex cursor-pointer items-center gap-2 border border-border px-3.5 py-2.5 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <ImageUp className="h-3.5 w-3.5" strokeWidth={1.5} />
          )}
          {busy ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          className="hidden"
          onChange={e => {
            void pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              setNote(null);
            }}
            className="inline-flex cursor-pointer items-center gap-1 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:text-destructive"
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
            Clear
          </button>
        )}
      </div>

      <input
        type="url"
        value={value}
        onChange={e => {
          onChange(e.target.value);
          setNote(null);
        }}
        placeholder="https://…"
        className={cn(FIELD, "mt-4")}
      />

      {fieldError ? (
        <span className="mt-1.5 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
          {fieldError}
        </span>
      ) : note ? (
        <span className="mt-1.5 block">
          <Banner tone={note.tone}>{note.text}</Banner>
        </span>
      ) : null}

      {!fieldError && size && (
        <span className="mt-1.5 block font-sans text-[0.7rem] text-muted-foreground/70">
          {describeSize(size, measured)}
        </span>
      )}

      {value && (
        <img
          src={value}
          alt=""
          className="mt-4 h-28 w-full border border-border object-cover"
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------- portfolio tab */

type PortfolioDraft = {
  /** Present only while editing an existing row. */
  id?: number;
  title: string;
  category: string;
  description: string;
  imageUrl: string;
  /** Intrinsic size, measured from the chosen image. */
  width: number | null;
  height: number | null;
  sortOrder: number;
  published: boolean;
};

const EMPTY_PORTFOLIO: PortfolioDraft = {
  title: "",
  category: "",
  description: "",
  imageUrl: "",
  width: null,
  height: null,
  sortOrder: 0,
  published: true,
};

function toDraft(item: PortfolioItem): PortfolioDraft {
  return {
    title: item.title,
    category: item.category,
    description: item.description,
    imageUrl: item.imageUrl,
    width: item.width,
    height: item.height,
    sortOrder: item.sortOrder,
    published: item.published,
  };
}

function PortfolioPanel() {
  const [items, setItems] = useState<PortfolioItem[] | null>(null);
  const [draft, setDraft] = useState<PortfolioDraft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [note, setNote] = useState<{ tone: "error" | "ok"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchAdminPortfolio();
    if (data.ok) setItems(data.items);
    else {
      setItems([]);
      setNote({ tone: "error", text: data.error });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof PortfolioDraft>(key: K, value: PortfolioDraft[K]) =>
    setDraft(prev => (prev ? { ...prev, [key]: value } : prev));

  const submit = async () => {
    if (!draft) return;
    setBusy(true);
    setErrors({});
    setNote(null);
    const editing = items?.some(i => i.id === draft.id) ?? false;
    const body = { ...draft, id: draft.id };
    const result = editing
      ? await updatePortfolioItem(body)
      : await createPortfolioItem(body);
    setBusy(false);

    if (!result.ok) {
      setErrors(result.fields ?? {});
      setNote({ tone: "error", text: result.error ?? "Could not save." });
      return;
    }
    setDraft(null);
    setNote({ tone: "ok", text: editing ? "Piece updated." : "Piece published to /art." });
    await load();
  };

  const remove = async (item: PortfolioItem) => {
    if (!confirm(`Delete “${item.title}”? This cannot be undone.`)) return;
    setNote(null);
    const result = await deletePortfolioItem(item.id);
    if (!result.ok) {
      setNote({ tone: "error", text: result.error ?? "Could not delete." });
      return;
    }
    setNote({ tone: "ok", text: "Piece deleted." });
    await load();
  };

  return (
    <>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <p className="font-sans text-xs leading-relaxed font-light text-muted-foreground">
          Pieces you publish here appear on <span className="text-foreground">/art</span>, newest
          first. Unpublished work stays hidden from visitors.
        </p>
        <button
          type="button"
          onClick={() => {
            setDraft({ ...EMPTY_PORTFOLIO, sortOrder: items?.length ?? 0 });
            setErrors({});
            setNote(null);
          }}
          className="inline-flex cursor-pointer items-center gap-2 bg-foreground px-4 py-2.5 font-sans text-[0.6rem] tracking-[0.16em] text-background uppercase transition-colors duration-300 hover:bg-gold"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Add piece
        </button>
      </div>

      {note && (
        <div className="mb-6">
          <Banner tone={note.tone}>{note.text}</Banner>
        </div>
      )}

      {draft && (
        <section className="mb-10 border border-border bg-card/50 p-6 sm:p-8">
          <h2 className="font-display text-xl text-foreground">
            {draft.id ? "Edit piece" : "New piece"}
          </h2>

          <div className="mt-2 grid gap-x-8 sm:grid-cols-2">
            <Field label="Title" error={errors.title}>
              <input
                value={draft.title}
                onChange={e => set("title", e.target.value)}
                placeholder="Bridal — soft glam"
                className={FIELD}
              />
            </Field>
            <Field
              label="Category"
              hint="Used by the filter row on /art."
              error={errors.category}
            >
              <input
                value={draft.category}
                onChange={e => set("category", e.target.value)}
                placeholder="Bridal"
                className={FIELD}
              />
            </Field>
          </div>

          <Field label="Description" error={errors.description}>
            <textarea
              value={draft.description}
              onChange={e => set("description", e.target.value)}
              rows={3}
              placeholder="One or two lines about the finish."
              className={cn(FIELD, "resize-y")}
            />
          </Field>

          <div className="mt-5">
            <ImageField
              value={draft.imageUrl}
              size={draft.width && draft.height ? { width: draft.width, height: draft.height } : null}
              onChange={next => set("imageUrl", next)}
              onSizeChange={next =>
                setDraft(prev => (prev ? { ...prev, width: next?.width ?? null, height: next?.height ?? null } : prev))
              }
              folder="portfolio"
              fieldError={errors.imageUrl ?? errors.imageKey}
            />
          </div>

          <div className="mt-5 grid gap-x-8 sm:grid-cols-2">
            <Field label="Order" hint="Lower numbers appear first." error={errors.sortOrder}>
              <input
                type="number"
                value={draft.sortOrder}
                onChange={e => set("sortOrder", Number(e.target.value))}
                className={FIELD}
              />
            </Field>
            <div className={cn(LABEL, "sm:pt-[1.6rem]")}>
              <Toggle
                checked={draft.published}
                onChange={next => set("published", next)}
                label={draft.published ? "Published" : "Hidden"}
              />
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy}
              className="inline-flex cursor-pointer items-center gap-2 bg-foreground px-5 py-3 font-sans text-[0.6rem] tracking-[0.16em] text-background uppercase transition-colors duration-300 hover:bg-gold disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              {busy ? "Saving…" : "Save piece"}
            </button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="cursor-pointer border border-border px-5 py-3 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {items === null ? (
        <div className="flex items-center gap-2 py-10 font-sans text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center font-sans text-xs text-muted-foreground">
          Nothing here yet. Add your first piece to populate /art.
        </p>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[46rem]">
            <thead className="border-b border-border bg-card/40">
              <tr>
                <Th className="w-20">Image</Th>
                <Th>Title</Th>
                <Th>Category</Th>
                <Th className="w-24 text-right">Order</Th>
                <Th className="w-32">Live</Th>
                <Th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-3">
                    <img
                      src={item.src}
                      alt=""
                      loading="lazy"
                      className="h-12 w-16 border border-border object-cover"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-sans text-sm text-foreground">{item.title}</span>
                  </td>
                  <td className="px-3 py-3 font-sans text-xs text-muted-foreground">
                    {item.category || "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-sans text-xs tabular-nums text-muted-foreground">
                    {item.sortOrder}
                  </td>
                  <td className="px-3 py-3">
                    <Toggle
                      checked={item.published}
                      onChange={async next => {
                        await updatePortfolioItem({ id: item.id, published: next });
                        await load();
                      }}
                      label={item.published ? "Live" : "Hidden"}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <span className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setDraft({ ...toDraft(item), id: item.id });
                          setErrors({});
                          setNote(null);
                        }}
                        aria-label={`Edit ${item.title}`}
                        className="cursor-pointer border border-border p-2 text-muted-foreground transition-colors hover:border-gold hover:text-gold"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(item)}
                        aria-label={`Delete ${item.title}`}
                        className="cursor-pointer border border-border p-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- ads tab */

type AdDraft = {
  headline: string;
  kicker: string;
  body: string;
  imageUrl: string;
  ctaLabel: string;
  ctaUrl: string;
  startsAt: string;
  endsAt: string;
  width: number | null;
  height: number | null;
  sortOrder: number;
  published: boolean;
};

const EMPTY_AD: AdDraft = {
  headline: "",
  kicker: "",
  body: "",
  imageUrl: "",
  ctaLabel: "",
  ctaUrl: "",
  startsAt: "",
  endsAt: "",
  width: null,
  height: null,
  sortOrder: 0,
  published: true,
};

function toAdDraft(item: Ad): AdDraft & { id?: number } {
  return {
    id: item.id,
    headline: item.headline,
    kicker: item.kicker,
    body: item.body,
    imageUrl: item.imageUrl,
    ctaLabel: item.ctaLabel,
    ctaUrl: item.ctaUrl,
    startsAt: item.startsAt ?? "",
    endsAt: item.endsAt ?? "",
    width: item.width,
    height: item.height,
    sortOrder: item.sortOrder,
    published: item.published,
  };
}

function AdsPanel() {
  const [items, setItems] = useState<Ad[] | null>(null);
  const [draft, setDraft] = useState<(AdDraft & { id?: number }) | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [note, setNote] = useState<{ tone: "error" | "ok"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchAdminAds();
    if (data.ok) setItems(data.items);
    else {
      setItems([]);
      setNote({ tone: "error", text: data.error });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const set = <K extends keyof AdDraft>(key: K, value: AdDraft[K]) =>
    setDraft(prev => (prev ? { ...prev, [key]: value } : prev));

  const submit = async () => {
    if (!draft) return;
    setBusy(true);
    setErrors({});
    setNote(null);
    // Empty date inputs mean "no bound", which the API stores as null.
    const body = {
      ...draft,
      startsAt: draft.startsAt || null,
      endsAt: draft.endsAt || null,
    };
    const result = draft.id ? await updateAd(body) : await createAd(body);
    setBusy(false);

    if (!result.ok) {
      setErrors(result.fields ?? {});
      setNote({ tone: "error", text: result.error ?? "Could not save." });
      return;
    }
    setDraft(null);
    setNote({ tone: "ok", text: draft.id ? "Announcement updated." : "Announcement published to /ad." });
    await load();
  };

  const remove = async (item: Ad) => {
    if (!confirm(`Delete “${item.headline}”?`)) return;
    setNote(null);
    const result = await deleteAd(item.id);
    if (!result.ok) {
      setNote({ tone: "error", text: result.error ?? "Could not delete." });
      return;
    }
    setNote({ tone: "ok", text: "Announcement deleted." });
    await load();
  };

  return (
    <>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <p className="font-sans text-xs leading-relaxed font-light text-muted-foreground">
          Announcements appear on <span className="text-foreground">/ad</span>. Set a start or
          end date and the card retires itself once that day passes.
        </p>
        <button
          type="button"
          onClick={() => {
            setDraft({ ...EMPTY_AD, sortOrder: items?.length ?? 0 });
            setErrors({});
            setNote(null);
          }}
          className="inline-flex cursor-pointer items-center gap-2 bg-foreground px-4 py-2.5 font-sans text-[0.6rem] tracking-[0.16em] text-background uppercase transition-colors duration-300 hover:bg-gold"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Add announcement
        </button>
      </div>

      {note && (
        <div className="mb-6">
          <Banner tone={note.tone}>{note.text}</Banner>
        </div>
      )}

      {draft && (
        <section className="mb-10 border border-border bg-card/50 p-6 sm:p-8">
          <h2 className="font-display text-xl text-foreground">
            {draft.id ? "Edit announcement" : "New announcement"}
          </h2>

          <div className="mt-2 grid gap-x-8 sm:grid-cols-2">
            <Field label="Headline" error={errors.headline}>
              <input
                value={draft.headline}
                onChange={e => set("headline", e.target.value)}
                placeholder="Valentine's bookings are open"
                className={FIELD}
              />
            </Field>
            <Field label="Kicker" hint="Small label above the headline." error={errors.kicker}>
              <input
                value={draft.kicker}
                onChange={e => set("kicker", e.target.value)}
                placeholder="Seasonal"
                className={FIELD}
              />
            </Field>
          </div>

          <Field label="Body" error={errors.body}>
            <textarea
              value={draft.body}
              onChange={e => set("body", e.target.value)}
              rows={3}
              placeholder="What is included, and who it suits."
              className={cn(FIELD, "resize-y")}
            />
          </Field>

          <div className="mt-5">
            <ImageField
              value={draft.imageUrl}
              size={draft.width && draft.height ? { width: draft.width, height: draft.height } : null}
              onChange={next => set("imageUrl", next)}
              onSizeChange={next =>
                setDraft(prev => (prev ? { ...prev, width: next?.width ?? null, height: next?.height ?? null } : prev))
              }
              folder="ads"
              fieldError={errors.imageUrl ?? errors.imageKey}
            />
          </div>

          <div className="mt-5 grid gap-x-8 sm:grid-cols-2">
            <Field label="Button label" error={errors.ctaLabel}>
              <input
                value={draft.ctaLabel}
                onChange={e => set("ctaLabel", e.target.value)}
                placeholder="Book a date"
                className={FIELD}
              />
            </Field>
            <Field label="Button link" error={errors.ctaUrl}>
              <input
                value={draft.ctaUrl}
                onChange={e => set("ctaUrl", e.target.value)}
                placeholder="https://wa.me/…"
                className={FIELD}
              />
            </Field>
          </div>

          <div className="mt-5 grid gap-x-8 sm:grid-cols-2">
            <Field label="Starts" hint="Leave empty to publish immediately." error={errors.startsAt}>
              <input
                type="date"
                value={draft.startsAt}
                onChange={e => set("startsAt", e.target.value)}
                className={FIELD}
              />
            </Field>
            <Field label="Ends" hint="Leave empty to run indefinitely." error={errors.endsAt}>
              <input
                type="date"
                value={draft.endsAt}
                onChange={e => set("endsAt", e.target.value)}
                className={FIELD}
              />
            </Field>
          </div>

          <div className="mt-5 grid gap-x-8 sm:grid-cols-2">
            <Field label="Order" hint="Lower numbers appear first." error={errors.sortOrder}>
              <input
                type="number"
                value={draft.sortOrder}
                onChange={e => set("sortOrder", Number(e.target.value))}
                className={FIELD}
              />
            </Field>
            <div className={cn(LABEL, "sm:pt-[1.6rem]")}>
              <Toggle
                checked={draft.published}
                onChange={next => set("published", next)}
                label={draft.published ? "Published" : "Hidden"}
              />
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={busy}
              className="inline-flex cursor-pointer items-center gap-2 bg-foreground px-5 py-3 font-sans text-[0.6rem] tracking-[0.16em] text-background uppercase transition-colors duration-300 hover:bg-gold disabled:opacity-50"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              {busy ? "Saving…" : "Save announcement"}
            </button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="cursor-pointer border border-border px-5 py-3 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold"
            >
              Cancel
            </button>
          </div>
        </section>
      )}

      {items === null ? (
        <div className="flex items-center gap-2 py-10 font-sans text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center font-sans text-xs text-muted-foreground">
          No announcements yet. /ad will show its empty state until you add one.
        </p>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[46rem]">
            <thead className="border-b border-border bg-card/40">
              <tr>
                <Th className="w-20">Image</Th>
                <Th>Headline</Th>
                <Th className="w-44">Window</Th>
                <Th className="w-24 text-right">Order</Th>
                <Th className="w-32">Live</Th>
                <Th className="w-28" />
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-3">
                    {item.src ? (
                      <img
                        src={item.src}
                        alt=""
                        loading="lazy"
                        className="h-12 w-16 border border-border object-cover"
                      />
                    ) : (
                      <span className="flex h-12 w-16 items-center justify-center border border-border font-sans text-[0.55rem] text-muted-foreground">
                        None
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-sans text-sm text-foreground">{item.headline}</span>
                  </td>
                  <td className="px-3 py-3 font-sans text-xs tabular-nums text-muted-foreground">
                    {item.startsAt ?? "—"} → {item.endsAt ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-sans text-xs tabular-nums text-muted-foreground">
                    {item.sortOrder}
                  </td>
                  <td className="px-3 py-3">
                    <Toggle
                      checked={item.published}
                      onChange={async next => {
                        await updateAd({ id: item.id, published: next });
                        await load();
                      }}
                      label={item.published ? "Live" : "Hidden"}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <span className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setDraft(toAdDraft(item));
                          setErrors({});
                          setNote(null);
                        }}
                        aria-label={`Edit ${item.headline}`}
                        className="cursor-pointer border border-border p-2 text-muted-foreground transition-colors hover:border-gold hover:text-gold"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(item)}
                        aria-label={`Delete ${item.headline}`}
                        className="cursor-pointer border border-border p-2 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ shell */

type ContentManagerProps = {
  tab: AdminTab;
  onTab: (tab: AdminTab) => void;
  onSignOut: () => void;
};

export function ContentManager({ tab, onTab, onSignOut }: ContentManagerProps) {
  const isPortfolio = tab === "portfolio";

  return (
    <div className="min-h-dvh bg-background">
      <AdminChrome
        title={isPortfolio ? "Portfolio" : "Announcements"}
        tab={tab}
        onTab={onTab}
        onSignOut={onSignOut}
      />
      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8">{isPortfolio ? <PortfolioPanel /> : <AdsPanel />}</main>
    </div>
  );
}
